import { supabase } from "@/integrations/supabase/client";
import { siteImageCatalog, type CatalogImage } from "@/lib/sitemapImages";

export type GallerySetting = {
  image_key: string;
  alt_text: string | null;
  title: string | null;
  caption: string | null;
  include_in_gallery: boolean;
  sort_order: number;
};

export type GalleryItem = CatalogImage & {
  alt: string;
  displayTitle: string;
  displayCaption: string;
  include: boolean;
  sort: number;
};

export function mergeGallery(settings: GallerySetting[], publicOnly = false): GalleryItem[] {
  const byKey = new Map(settings.map((s) => [s.image_key, s]));
  const seen = new Set<string>();
  return siteImageCatalog()
    .map((im, i) => {
      const s = byKey.get(im.key);
      // Same photo in several sizes/formats: show only the first by default. Logo hidden by default.
      const group = im.key.replace(/\.\w+$/, "").replace(/-(\d+|optimized|card)+$/g, "");
      const defaultInclude = !seen.has(group) && !/logo/i.test(im.key);
      seen.add(group);
      return {
        ...im,
        alt: s?.alt_text || im.title,
        displayTitle: s?.title || im.title,
        displayCaption: s?.caption || im.caption,
        include: s ? s.include_in_gallery : defaultInclude,
        sort: s?.sort_order ?? 1000 + i,
      };
    })
    .filter((im) => !publicOnly || (byKey.has(im.key) && im.include))
    .sort((a, b) => a.sort - b.sort);
}

export async function fetchGallerySettings(): Promise<GallerySetting[]> {
  const { data, error } = await supabase
    .from("gallery_images" as never)
    .select("image_key, alt_text, title, caption, include_in_gallery, sort_order");
  if (error) throw new Error("Gallery settings unavailable");
  return (data as unknown as GallerySetting[]) || [];
}
